import { Component, OnInit } from '@angular/core';

import { CropDefault, OTHER, FIELD, NURSERY, BRAND_ALBION, BY_COMMODITY } from './crop-type-match-catalog';

@Component({
	selector: 'crop-type-match-proto',
	templateUrl: './crop-type-match-proto.component.html',
	styleUrls: ['./crop-type-match-proto.component.scss']
})
export class CropTypeMatchProtoComponent implements OnInit {
	readonly OTHER = OTHER;
	readonly NURSERY = NURSERY;
	readonly FIELD = FIELD;
	readonly BRAND_ALBION = BRAND_ALBION;

	genericToCommodity: { [gn: string]: string } = {};
	allGenericNames: string[] = [];

	typeaheadQuery = '';
	typeaheadOpen = false;
	filteredGenericNames: string[] = [];

	commodity: string | null = null;
	genericName: string | null = null;
	plantingSystem: string | null = null;
	brand: string | null = null;
	region: string | null = null;
	season: string | null = null;
	productClass: string | null = null;
	age: string | null = null;
	bedWidth: number | null = null;
	rows: number | null = null;
	matchedName: string | null = null;

	ngOnInit(): void {
		this.buildGenericIndex();
		this.filteredGenericNames = this.allGenericNames.slice();
	}

	buildGenericIndex(): void {
		this.genericToCommodity = {};
		this.allGenericNames = [];

		Object.keys(BY_COMMODITY).forEach(commodity => {
			BY_COMMODITY[commodity].forEach(row => {
				let gn = row.genericName;

				if (!gn) {
					return;
				}

				this.genericToCommodity[gn] = commodity;

				if (this.allGenericNames.indexOf(gn) < 0) {
					this.allGenericNames.push(gn);
				}
			});
		});

		this.allGenericNames.sort(function (a, b) {
			return a.localeCompare(b);
		});
	}

	defaults(): CropDefault[] {
		return this.commodity ? BY_COMMODITY[this.commodity] : [];
	}

	onTypeaheadChange(): void {
		let q = (this.typeaheadQuery || '').trim().toLowerCase();

		this.filteredGenericNames = !q
			? this.allGenericNames.slice()
			: this.allGenericNames.filter(g => g.toLowerCase().indexOf(q) >= 0);
		this.typeaheadOpen = true;

		// Typing clears a prior selection unless query still equals selected name
		if (this.genericName && this.typeaheadQuery !== this.genericName) {
			this.commodity = null;
			this.genericName = null;
			this.plantingSystem = null;
			this.resetAnswers();
		}
	}

	onTypeaheadBlur(): void {
		setTimeout(() => { this.typeaheadOpen = false; }, 150);
	}

	selectGenericName(gn: string): void {
		this.genericName = gn;
		this.commodity = this.genericToCommodity[gn];
		this.typeaheadQuery = gn;
		this.typeaheadOpen = false;
		this.plantingSystem = null;
		this.resetAnswers();
		this.resolve();
	}

	showPlantingSystem(): boolean {
		if (!this.genericName) {
			return false;
		}

		let pool = this.defaults().filter(d => d.genericName === this.genericName);

		return pool.some(d => !!d.plantingSystem) && pool.length > 1;
	}

	/** Catalog rows after top-level closed filters */
	basePool(): CropDefault[] {
		if (!this.commodity || !this.genericName) {
			return [];
		}

		let list = this.defaults().filter(d => d.genericName === this.genericName);

		if (this.showPlantingSystem()) {
			if (!this.plantingSystem) {
				return [];
			}

			list = this.plantingSystem === NURSERY
				? list.filter(d => d.plantingSystem === NURSERY)
				: list.filter(d => d.plantingSystem !== NURSERY);
		}

		return list;
	}

	regionOptions(): string[] {
		return unique(this.basePool().map(d => d.region).filter(Boolean) as string[]);
	}

	showRegion(): boolean {
		// Only when region actually splits the pool (2+ tags, or tag + unscoped → Other)
		return hasChoice(this.regionOptions(), this.basePool(), d => d.region);
	}

	afterRegion(): CropDefault[] {
		if (!this.showRegion() || !this.region) {
			return this.basePool();
		}

		if (this.region === OTHER) {
			let unscoped = this.basePool().filter(d => d.region == null);

			if (unscoped.length) {
				return unscoped;
			}

			let fallback = this.regionOptions()[0];

			return this.basePool().filter(d => d.region === fallback);
		}

		return this.basePool().filter(d => d.region === this.region);
	}

	seasonOptions(): string[] {
		return unique(this.afterRegion().map(d => d.season).filter(Boolean) as string[]);
	}

	showSeason(): boolean {
		if (this.showRegion() && !this.region) {
			return false;
		}

		return hasChoice(this.seasonOptions(), this.afterRegion(), d => d.season);
	}

	afterSeason(): CropDefault[] {
		if (!this.showSeason() || !this.season) {
			return this.afterRegion();
		}

		if (this.season === OTHER) {
			let unscoped = this.afterRegion().filter(d => d.season == null);

			if (unscoped.length) {
				return unscoped;
			}

			let fallback = this.seasonOptions()[0];

			return this.afterRegion().filter(d => d.season === fallback);
		}

		return this.afterRegion().filter(d => d.season === this.season);
	}

	productClassOptions(): string[] {
		return unique(this.afterSeason().map(d => d.productClass).filter(Boolean) as string[]);
	}

	showProductClass(): boolean {
		if (this.showRegion() && !this.region) {
			return false;
		}

		if (this.showSeason() && !this.season) {
			return false;
		}

		return hasChoice(this.productClassOptions(), this.afterSeason(), d => d.productClass);
	}

	afterProductClass(): CropDefault[] {
		if (!this.showProductClass() || !this.productClass) {
			return this.afterSeason();
		}

		if (this.productClass === OTHER) {
			let unscoped = this.afterSeason().filter(d => d.productClass == null);

			if (unscoped.length) {
				return unscoped;
			}

			let fallback = this.productClassOptions()[0];

			return this.afterSeason().filter(d => d.productClass === fallback);
		}

		return this.afterSeason().filter(d => d.productClass === this.productClass);
	}

	ageOptions(): string[] {
		return unique(this.afterProductClass().map(d => d.age).filter(Boolean) as string[]);
	}

	showAge(): boolean {
		if (this.showRegion() && !this.region) {
			return false;
		}

		if (this.showSeason() && !this.season) {
			return false;
		}

		if (this.showProductClass() && !this.productClass) {
			return false;
		}

		return hasChoice(this.ageOptions(), this.afterProductClass(), d => d.age);
	}

	afterAge(): CropDefault[] {
		if (!this.showAge() || !this.age) {
			return this.afterProductClass();
		}

		if (this.age === OTHER) {
			let unscoped = this.afterProductClass().filter(d => d.age == null);

			if (unscoped.length) {
				return unscoped;
			}

			let fallback = this.ageOptions()[0];

			return this.afterProductClass().filter(d => d.age === fallback);
		}

		return this.afterProductClass().filter(d => d.age === this.age);
	}

	showBrand(): boolean {
		if (this.showRegion() && !this.region) {
			return false;
		}

		if (this.showSeason() && !this.season) {
			return false;
		}

		if (this.showProductClass() && !this.productClass) {
			return false;
		}

		if (this.showAge() && !this.age) {
			return false;
		}

		let pool = this.afterAge();
		let hasAlbion = pool.some(d => d.brand === BRAND_ALBION);
		let hasOtherBrand = pool.some(d => d.brand && d.brand !== BRAND_ALBION);

		return hasAlbion && hasOtherBrand;
	}

	afterBrand(): CropDefault[] {
		if (!this.showBrand() || !this.brand) {
			return this.afterAge();
		}

		return this.brand === BRAND_ALBION
			? this.afterAge().filter(d => d.brand === BRAND_ALBION)
			: this.afterAge().filter(d => d.brand !== BRAND_ALBION);
	}

	/** Ask bed/rows only when more than one candidate remains after exact filters */
	showBedRows(): boolean {
		if (!this.genericName) {
			return false;
		}

		if (this.showPlantingSystem() && !this.plantingSystem) {
			return false;
		}

		if (this.showRegion() && !this.region) {
			return false;
		}

		if (this.showSeason() && !this.season) {
			return false;
		}

		if (this.showProductClass() && !this.productClass) {
			return false;
		}

		if (this.showAge() && !this.age) {
			return false;
		}

		if (this.showBrand() && !this.brand) {
			return false;
		}

		return this.afterBrand().length > 1;
	}

	resetAnswers(): void {
		this.region = null;
		this.season = null;
		this.productClass = null;
		this.age = null;
		this.brand = null;
		this.bedWidth = null;
		this.rows = null;
		this.matchedName = null;
	}

	resolve(): void {
		this.matchedName = null;

		if (!this.commodity || !this.genericName) {
			return;
		}

		if (this.showPlantingSystem() && !this.plantingSystem) {
			return;
		}

		if (this.showRegion() && !this.region) {
			return;
		}

		if (this.showSeason() && !this.season) {
			return;
		}

		if (this.showProductClass() && !this.productClass) {
			return;
		}

		if (this.showAge() && !this.age) {
			return;
		}

		if (this.showBrand() && !this.brand) {
			return;
		}

		let pool = this.afterBrand();

		if (!pool.length) {
			return;
		}

		if (this.showBedRows()) {
			if (this.bedWidth == null || this.rows == null) {
				return;
			}

			this.matchedName = nearest(pool, this.bedWidth, this.rows);
			return;
		}

		this.matchedName = pool[0].name;
	}
}

function nearest(candidates: CropDefault[], bed: number, rows: number): string | null {
	if (!candidates.length) {
		return null;
	}

	let best = candidates[0];
	let bestScore = Infinity;

	for (let c of candidates) {
		let bedDist = c.bedWidth == null ? 0 : Math.abs(c.bedWidth - bed);
		let rowDist = c.rows == null ? 0 : Math.abs(c.rows - rows);
		let score = bedDist + rowDist;

		if (score < bestScore) {
			bestScore = score;
			best = c;
		}
	}

	return best.name;
}

function unique(vals: string[]): string[] {
	return vals.filter((v, i, a) => a.indexOf(v) === i);
}

/** Show a tagged axis only if it splits the pool (multiple values, or tag + unscoped). */
function hasChoice(
	options: string[],
	pool: CropDefault[],
	get: (d: CropDefault) => string | null | undefined
): boolean {
	if (options.length > 1) {
		return true;
	}

	if (options.length === 1 && pool.some(d => !get(d))) {
		return true;
	}

	return false;
}
